import { materialRenderers, materialCells } from '@jsonforms/material-renderers';
import { JsonForms } from '@jsonforms/react';
import {
  Box,
  useTheme,
  Typography,
  Divider,
  Button,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EnhancedWidgets, { GoogleMapsAddressPicker } from './EnhancedWidgets';

// Helper to build a two-column uischema if wide enough and enough fields
function buildTwoColumnUiSchema(schema, originalUiSchema, isWide) {
  if (!isWide || !schema?.properties || Object.keys(schema.properties).length < 2) {
    return originalUiSchema;
  }
  const fields = Object.keys(schema.properties);
  const mid = Math.ceil(fields.length / 2);
  const left = fields.slice(0, mid);
  const right = fields.slice(mid);

  return {
    type: 'Group',
    label: '',
    elements: [
      {
        type: 'VerticalLayout',
        elements: left.map((field) => ({
          type: 'Control',
          scope: `#/properties/${field}`,
        })),
      },
      {
        type: 'VerticalLayout',
        elements: right.map((field) => ({
          type: 'Control',
          scope: `#/properties/${field}`,
        })),
      },
    ],
    options: { direction: 'row', spacing: 2 },
  };
}

// Helper: map widget types to valid JSON Schema types
function getJsonSchemaType(widgetType) {
  switch (widgetType) {
    case 'number':
      return 'number';
    case 'checkbox':
      return 'boolean';
    case 'date':
    case 'datetime':
    case 'text':
    case 'textarea':
    case 'listbox':
    case 'combobox':
    case 'radiobuttons':
    case 'address':
      return 'string';
    default:
      return 'string';
  }
}

// Helper: extract schema/uischema from JSON code if present, else use as-is
function getSchemaAndUiSchema({ schema, uischema, formElements }) {
  if (formElements && Array.isArray(formElements)) {
    const gridColumnKeys = formElements
      .filter((el) => el.type === 'grid')
      .flatMap((el) => (el.columns || []).map((col) => col.key || col.id));
    const groupBoxFieldIds = formElements
      .filter((el) => el.type === 'groupbox')
      .flatMap((el) => el.fields || []);

    // --- Build schema: groupbox as a flat set of fields at the root, but grouped in uischema ---
    const properties = formElements.reduce((acc, el) => {
      if (el.type === 'grid') {
        acc[el.id] = {
          type: 'array',
          title: el.label,
          items: {
            type: 'object',
            properties: Array.isArray(el.columns)
              ? el.columns.reduce((cols, col) => {
                cols[col.key || col.id || `col_${Math.random()}`] = {
                  type: getJsonSchemaType(col.type),
                  title: col.label || col.key || col.type,
                };
                return cols;
              }, {})
              : {},
          },
        };
        return acc;
      }
      // Do NOT add groupbox itself to schema, only its fields at root
      if (el.type === 'groupbox') {
        return acc;
      }
      // Exclude fields that are columns in any grid
      if (
        gridColumnKeys.includes(el.id)
        || gridColumnKeys.includes(el.key)
      ) return acc;
      // Groupbox fields are at root, so do not exclude them here
      const type = getJsonSchemaType(el.type);
      const elementSchema = { type, title: el.label };
      if (el.type === 'date') elementSchema.format = 'date';
      if (el.type === 'datetime') elementSchema.format = 'date-time';
      if (el.options && el.options.length) elementSchema.enum = el.options;
      if (el.minLength) elementSchema.minLength = el.minLength;
      if (el.maxLength) elementSchema.maxLength = el.maxLength;
      if (el.pattern) elementSchema.pattern = el.pattern;
      if (el.minimum) elementSchema.minimum = el.minimum;
      if (el.maximum) elementSchema.maximum = el.maximum;
      if (el.multipleOf) elementSchema.multipleOf = el.multipleOf;
      // Remove "required: true" from property level (not valid in JSON Schema for properties)
      acc[el.key || el.id] = elementSchema;
      return acc;
    }, {});
    const runtimeSchema = { type: 'object', properties };

    // --- Build uischema: groupbox as Group with controls for its fields (fields at root) ---
    const runtimeUiSchema = {
      type: 'VerticalLayout',
      elements: formElements
        .filter((el) => {
          if (el.type === 'grid') return true;
          if (el.type === 'groupbox') return true;
          if (
            gridColumnKeys.includes(el.id)
            || gridColumnKeys.includes(el.key)
          ) return false;
          return true;
        })
        .map((el) => {
          const options = {
            format: el.type === 'checkbox' ? 'checkbox' : el.type,
            enum: el.options,
            minLength: el.minLength,
            maxLength: el.maxLength,
            pattern: el.pattern,
            minimum: el.minimum,
            maximum: el.maximum,
            multipleOf: el.multipleOf,
          };
          if (el.type === 'date') options.format = 'date';
          if (el.type === 'datetime') options.format = 'date-time';
          if (el.type === 'textarea') {
            options.format = 'textarea';
            options.multi = true;
            options.rows = el.rows || 4;
          }
          if (el.type === 'grid') {
            return {
              type: 'Control',
              scope: `#/properties/${el.id}`,
              options: { table: true },
            };
          }
          if (el.type === 'groupbox') {
            // Render a group with controls for each field (fields are at root)
            return {
              type: 'Group',
              label: el.label,
              elements: (el.fields || []).map((fieldId) => ({
                type: 'Control',
                scope: `#/properties/${fieldId}`,
              })),
            };
          }
          return {
            type: 'Control',
            scope: `#/properties/${el.key || el.id}`,
            options,
          };
        }),
    };

    return { schema: runtimeSchema, uischema: runtimeUiSchema };
  }

  // If schema/uischema are provided directly (from JSON), use them
  return { schema, uischema };
}

/**
 * FormRuntime renders a form at runtime using JSONForms.
 * Accepts either { schema, uischema } (from JSONEditor) or { formElements } (from FormEditor).
 */
function FormRuntime({ formElements, initialData }) {
  const theme = useTheme();

  // --- Generate schema and uischema, but filter out groupbox fields from top-level elements ---

  const getJsonSchemaType = (widgetType) => {
    switch (widgetType) {
      case 'number':
        return 'number';
      case 'checkbox':
        return 'boolean';
      case 'date':
      case 'datetime':
      case 'text':
      case 'textarea':
      case 'listbox':
      case 'combobox':
      case 'radiobuttons':
        return 'string';
      default:
        return 'string';
    }
  };

  const getAllGridColumnKeys = () => formElements
    .filter((el) => el.type === 'grid')
    .flatMap((el) => (el.columns || []).map((col) => col.key));
  const getAllGroupboxFieldIds = () => formElements
    .filter((el) => el.type === 'groupbox')
    .flatMap((el) => el.fields || []);

  const gridColumnKeys = getAllGridColumnKeys();
  const groupboxFieldIds = getAllGroupboxFieldIds();

  const schema = {
    type: 'object',
    properties: formElements.reduce((acc, el) => {
      if (el.type === 'grid') {
        acc[el.id] = {
          type: 'array',
          title: el.label,
          items: {
            type: 'object',
            properties: Array.isArray(el.columns)
              ? el.columns.reduce((cols, col) => {
                const colType = getJsonSchemaType(col.type);
                const colSchema = {
                  type: colType,
                  title: col.label || col.key || col.type,
                };
                // Add format for date/datetime
                if (col.type === 'date') colSchema.format = 'date';
                if (col.type === 'datetime') colSchema.format = 'date-time';
                if (col.options && col.options.length) colSchema.enum = col.options;
                if (col.minLength) colSchema.minLength = col.minLength;
                if (col.maxLength) colSchema.maxLength = col.maxLength;
                if (col.pattern) colSchema.pattern = col.pattern;
                if (col.minimum) colSchema.minimum = col.minimum;
                if (col.maximum) colSchema.maximum = col.maximum;
                if (col.multipleOf) colSchema.multipleOf = col.multipleOf;
                cols[col.key || col.id || `col_${Math.random()}`] = colSchema;
                return cols;
              }, {})
              : {},
          },
        };
        return acc;
      }
      if (el.type === 'groupbox') {
        acc[el.id] = {
          type: 'object',
          title: el.label,
          properties: (el.fields || []).reduce((fieldsAcc, fieldId) => {
            const field = formElements.find((f) => f.id === fieldId);
            if (!field) return fieldsAcc;
            const type = getJsonSchemaType(field.type);
            const fieldSchema = { type, title: field.label };
            if (field.type === 'date') fieldSchema.format = 'date';
            if (field.type === 'datetime') fieldSchema.format = 'date-time';
            if (field.options && field.options.length) fieldSchema.enum = field.options;
            if (field.minLength) fieldSchema.minLength = field.minLength;
            if (field.maxLength) fieldSchema.maxLength = field.maxLength;
            if (field.pattern) fieldSchema.pattern = field.pattern;
            if (field.minimum) fieldSchema.minimum = field.minimum;
            if (field.maximum) fieldSchema.maximum = field.maximum;
            if (field.multipleOf) fieldSchema.multipleOf = field.multipleOf;
            if (field.required) fieldSchema.required = true;
            fieldsAcc[field.id] = fieldSchema;
            return fieldsAcc;
          }, {}),
        };
        return acc;
      }
      // Exclude fields that are columns in any grid or in any groupbox
      if (
        gridColumnKeys.includes(el.id)
				|| gridColumnKeys.includes(el.key)
				|| groupboxFieldIds.includes(el.id)
      ) return acc;
      const type = getJsonSchemaType(el.type);
      const elementSchema = { type, title: el.label };
      if (el.type === 'date') elementSchema.format = 'date';
      if (el.type === 'datetime') elementSchema.format = 'date-time';
      if (el.options && el.options.length) elementSchema.enum = el.options;
      if (el.minLength) elementSchema.minLength = el.minLength;
      if (el.maxLength) elementSchema.maxLength = el.maxLength;
      if (el.pattern) elementSchema.pattern = el.pattern;
      if (el.minimum) elementSchema.minimum = el.minimum;
      if (el.maximum) elementSchema.maximum = el.maximum;
      if (el.multipleOf) elementSchema.multipleOf = el.multipleOf;
      if (el.required) elementSchema.required = true;
      acc[el.id] = elementSchema;
      return acc;
    }, {}),
  };

  const uischema = {
    type: 'VerticalLayout',
    elements: formElements
      .filter((el) => {
        if (el.type === 'grid' || el.type === 'groupbox') return true;
        if (
          gridColumnKeys.includes(el.id)
					|| gridColumnKeys.includes(el.key)
					|| groupboxFieldIds.includes(el.id)
        ) return false;
        return true;
      })
      .map((el) => {
        const options = {
          format: el.type === 'checkbox' ? 'checkbox' : el.type,
          enum: el.options,
          minLength: el.minLength,
          maxLength: el.maxLength,
          pattern: el.pattern,
          minimum: el.minimum,
          maximum: el.maximum,
          multipleOf: el.multipleOf,
        };
        if (el.type === 'date') options.format = 'date';
        if (el.type === 'datetime') options.format = 'date-time';
        if (el.type === 'textarea') {
          options.format = 'textarea';
          options.multi = true;
          options.rows = el.rows || 4;
        }
        if (el.type === 'grid') {
          return {
            type: 'Control',
            scope: `#/properties/${el.id}`,
            options: { table: true },
          };
        }
        if (el.type === 'groupbox') {
          return {
            type: 'Group',
            label: el.label,
            elements: (el.fields || []).map((fieldId) => ({
              type: 'Control',
              scope: `#/properties/${el.id}/properties/${fieldId}`,
            })),
          };
        }
        return {
          type: 'Control',
          scope: `#/properties/${el.id}`,
          options,
        };
      }),
  };

  // Enhance uischema: add a visual marker for groupbox sections
  function enhanceUiSchemaWithGroupMarkers(uischema) {
    if (!uischema || !uischema.elements) return uischema;
    return {
      ...uischema,
      elements: uischema.elements.map((el) => {
        if (el.type === 'Group') {
          return {
            ...el,
            options: {
              ...(el.options || {}),
              renderAsSection: true, // custom marker for our wrapper
            },
          };
        }
        return el;
      }),
    };
  }

  const enhancedUiSchema = enhanceUiSchemaWithGroupMarkers(uischema);

  function GroupSectionWrapper({ label, children }) {
    return (
      <Box
        sx={{
			  mt: 2,
			  mb: 2,
			  px: 2,
			  py: 2,
			  border: `1px solid ${theme.palette.divider}`,
			  borderRadius: 2,
			  background: theme.palette.mode === 'dark'
			    ? theme.palette.background.default
			    : theme.palette.grey[50],
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {label}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {children}
      </Box>
    );
  }

  const customRenderers = [
    {
      tester: (uischema, schema) => (uischema.type === 'Group' && uischema.options?.renderAsSection ? 2 : -1),
      renderer: ({
        uischema, schema, path, renderers, cells, ...props
      }) => (
        <GroupSectionWrapper label={uischema.label}>
          <JsonForms
            schema={schema}
            uischema={{ ...uischema, type: 'VerticalLayout' }}
            path={path}
            renderers={renderers}
            cells={cells}
            {...props}
          />
        </GroupSectionWrapper>
      ),
    },
  ];

  const data = {}; // You may want to prefill with initialData or empty

  return (
    <Box
      sx={{
			  border: `1px solid ${theme.palette.divider}`,
			  backgroundColor: theme.palette.background.paper,
			  padding: 2,
			  overflowY: 'auto',
			  borderRadius: 1,
			  height: '100%',
			  color: theme.palette.text.primary,
			  display: 'flex',
			  flexDirection: 'column',
			  gap: 2,
      }}
    >
      <Box sx={{ width: '100%' }}>
        <JsonForms
          schema={schema}
          uischema={enhancedUiSchema}
          data={data}
          renderers={[...materialRenderers, ...customRenderers]}
          cells={materialCells} // <-- Add this line to fix "No applicable cell found."
          validationMode="validateOnChange"
        />
      </Box>
    </Box>
  );
}

// Move renderRuntimeWidget outside as a standalone function
const renderRuntimeWidget = (element, value, onChange, formData, formElements) => {
  const commonProps = {
    value: value || element.value || '',
    onChange,
    placeholder: element.placeholder,
    required: element.required,
    disabled: element.disabled,
    fullWidth: true,
    label: element.label,
  };

  switch (element.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'url':
      return (
        <TextField
          {...commonProps}
          type={element.type}
          variant="outlined"
        />
      );

    case 'textarea':
      return (
        <TextField
          {...commonProps}
          multiline
          rows={element.rows || 4}
          variant="outlined"
        />
      );

    case 'number':
      return (
        <TextField
          {...commonProps}
          type="number"
          inputProps={{
            min: element.min,
            max: element.max,
            step: element.step,
          }}
          variant="outlined"
        />
      );

    case 'date':
      return (
        <TextField
          {...commonProps}
          type="date"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'datetime':
      return (
        <TextField
          {...commonProps}
          type="datetime-local"
          variant="outlined"
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'checkbox':
      return (
        <FormControlLabel
          control={(
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
          )}
          label={element.label}
        />
      );

    case 'radiobuttons':
      return (
        <FormControl component="fieldset">
          <FormLabel component="legend">{element.label}</FormLabel>
          <RadioGroup value={value || ''} onChange={(e) => onChange(e.target.value)}>
            {(element.options || []).map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        </FormControl>
      );

    case 'combobox':
    case 'listbox':
      return (
        <FormControl fullWidth variant="outlined">
          <InputLabel>{element.label}</InputLabel>
          <Select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            label={element.label}
          >
            {(element.options || []).map((option, index) => (
              <MenuItem key={index} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );

    case 'grid':
      return (
        <Box>
          <Typography variant="h6">{element.label}</Typography>
          <Table>
            <TableHead>
              <TableRow>
                {(element.columns || []).map((col) => (
                  <TableCell key={col.id}>{col.label}</TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(value || []).map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {(element.columns || []).map((col) => (
                    <TableCell key={col.id}>
                      <TextField
                        value={row[col.key] || ''}
                        onChange={(e) => {
                          const newValue = [...(value || [])];
                          newValue[rowIndex] = {
                            ...newValue[rowIndex],
                            [col.key]: e.target.value,
                          };
                          onChange(newValue);
                        }}
                        variant="outlined"
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        const newValue = [...(value || [])];
                        newValue.splice(rowIndex, 1);
                        onChange(newValue);
                      }}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                const newRow = {};
                (element.columns || []).forEach((col) => {
                  newRow[col.key] = '';
                });
                const newValue = [...(value || []), newRow];
                onChange(newValue);
              }}
            >
              Add Row
            </Button>
          </Box>
        </Box>
      );

    case 'groupbox':
      return (
        <Box sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>{element.label}</Typography>
          {(element.fields || []).map((fieldId) => {
            const field = formElements.find((f) => f.id === fieldId);
            if (!field) return null;
            return (
              <Box key={fieldId} sx={{ mb: 2 }}>
                {renderRuntimeWidget(field, formData[fieldId], (newValue) => {
                  onChange({ ...formData, [fieldId]: newValue });
                }, formData, formElements)}
              </Box>
            );
          })}
        </Box>
      );

    case 'address':
      // Import the GoogleMapsAddressPicker component
      return (
        <GoogleMapsAddressPicker
          {...commonProps}
          apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
        />
      );

    default:
      return (
        <TextField
          {...commonProps}
          type="text"
          variant="outlined"
        />
      );
  }
};

// Main FormRuntime component that uses enhanced widgets
function FormRuntimeMain({
  formElements = [],
  formSettings = {},
  onSubmit,
  initialData = {},
  formId,
  isAuthenticated,
}) {
  const [formData, setFormData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  // Helper: count required fields
  const requiredFields = formElements.filter((el) => el.required && el.type !== 'groupbox' && el.type !== 'grid');
  const requiredFieldIds = requiredFields.map((el) => el.id);

  // Helper: count filled required fields
  const filledRequiredFields = requiredFieldIds.filter((id) => {
    const value = formData[id];
    return value !== undefined && value !== null && value !== '';
  });

  // Progress calculation
  const formProgress = requiredFields.length > 0
    ? (filledRequiredFields.length / requiredFields.length) * 100
    : 0;

  const handleInputChange = (elementId, value) => {
    const newData = { ...formData, [elementId]: value };
    setFormData(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(formData);
      setSubmitted(true);
      return;
    }
    if (formId) {
      // Log the endpoint and payload for debugging
      console.log('Submitting to endpoint:', isAuthenticated
        ? `/api/v1/forms/form-runtime/${formId}/submit`
        : `/api/v1/forms/form-runtime/public/${formId}/submit`);
      console.log('Payload:', { data: formData });

      const endpoint = isAuthenticated
        ? `/api/v1/forms/form-runtime/${formId}/submit`
        : `/api/v1/forms/form-runtime/public/${formId}/submit`;
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Add auth header if needed
          },
          body: JSON.stringify({ data: formData }),
        });
        const result = await res.json();
        // Log the result for debugging
        console.log('Server response:', result);
        // Handle result (success, error, etc.)
        setSubmitted(true); // Mark as submitted
      } catch (err) {
        // Log the error for debugging
        console.error('Error submitting form:', err);
      }
    }
  };

  const handleSubmitAnother = () => {
    setFormData(initialData || {});
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography variant="h5" color="success.main" gutterBottom>
          Form submitted successfully!
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" color="primary" onClick={handleSubmitAnother}>
            Submit Another
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        p: 3,
        flex: 1,
        minHeight: 0,
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Go Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
      {/* Form Header */}
      {formSettings.title && (
        <Typography variant="h4" gutterBottom>
          {formSettings.title}
        </Typography>
      )}
      {formSettings.description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {formSettings.description}
        </Typography>
      )}

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {Math.round(formProgress)}
          % Complete
        </Typography>
        <Box sx={{ width: '100%', mt: 0.5 }}>
          <Divider variant="fullWidth" sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 4 }}>
            <Box
              sx={{
                width: `${formProgress}%`,
                height: 8,
                bgcolor: formProgress === 100 ? 'success.main' : 'primary.main',
                borderRadius: 4,
                transition: 'width 0.3s',
              }}
            />
          </Divider>
        </Box>
      </Box>

      {/* Form Elements using enhanced widgets */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'auto',
        }}
      >
        {formElements.map((element) => {
          // Defensive: ensure element is defined and is an object
          if (!element || typeof element !== 'object') return null;
          // Defensive: always provide options/columns as arrays for all widgets
          const safeElement = {
            ...element,
            options: Array.isArray(element.options) ? element.options : [],
            columns: Array.isArray(element.columns) ? element.columns : [],
            fields: Array.isArray(element.fields) ? element.fields : [],
          };
          return (
            <Box key={safeElement.id}>
              {renderRuntimeWidget(
                safeElement,
                formData[safeElement.id] || safeElement.value || '',
                (value) => handleInputChange(safeElement.id, value),
                formData,
                formElements,
              )}
            </Box>
          );
        })}
      </Box>

      {/* Submit Button */}
      <Box
        sx={{
          mt: 4,
          flexShrink: 0,
          bgcolor: 'background.paper',
          // Optionally: position: 'sticky', bottom: 0, zIndex: 2, py: 2,
        }}
      >
        <Button
          type="submit"
          variant={formSettings.buttonStyle || 'contained'}
          size="large"
        >
          {formSettings.submitText || 'Submit'}
        </Button>
      </Box>
    </Box>
  );
}

// Export the main FormRuntime component
export default FormRuntimeMain;
